import { useState, useMemo, useCallback } from 'react';
import { components } from 'react-select';
import { useI18n } from '@locales/client';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { useCategories } from '../model/hooks';
import { useProviders } from '../../select-provider/model/hooks';
import { useGameSections } from '@/entities/game/model/hooks';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { processText } from '@/shared/libs/process-text';

import { Image } from '@/shared/ui/image';
import { Drawer } from '@/shared/ui/drawer';
import { SkeletonLoader } from '@/shared/ui/skeleton';
import {
  BodyTextBoldPrimary,
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';

import {
  OptionWrapper,
  SelectWrapper,
  StyledSelect,
  ControlWrapper,
  ImageOption,
  ImageControlWrapper,
  OptionCheck,
  EllipsisWrapper,
  OptionsWrapper,
  DrawerWrapper,
} from './styles';

const DEFAULT_CATEGORY_FILTER = 'all';
const IMAGE_SIZES = {
  MOBILE: 24,
  DESKTOP: 28,
};
const CHECK_ICON_SIZE = 16;
const CATEGORY_ICON_PATHS = {
  ALL: '/images/icons/all.svg',
  ALL_GRAY: '/images/icons/all-gray.svg',
  CHECK: '/images/icons/check.svg',
  FILTERS: '/images/icons/all-filters.svg',
  BONUS: '/images/icons/gift-fiters.svg',
};
const CATEGORY_IMAGE_PATH_PREFIX = 'assets/images/categories/icon_';
const CATEGORY_IMAGE_PATH_SUFFIX = '_normal.svg';
const BONUS_CATEGORY_ID = 'bonus';
const ALL_CATEGORY_ID = 'all';
const COLOR_SECONDARY = '#AFAFAF';
// const COLOR_BONUS = '#FFD60A';
const CATEGORY_TYPES = ['slots', 'instant', 'table', 'live'] as const;

const SelectCategory = ({ mobileLandscape }) => {
  const { categories, isLoading: isCategoriesLoading } = useCategories();
  const { providers, isLoading: isProvidersLoading } = useProviders();

  const { isUserLoading, notifications } = useUser();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const t = useI18n();
  const { games = [] } = useGameSections();

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const categoryFilter = searchParams.get('filter') ?? DEFAULT_CATEGORY_FILTER;

  const currentProviderTitle = useMemo(() => {
    const match = pathname?.match(/\/[a-z]{2}\/games\/([^\/?]+)/i);
    return match?.[1];
  }, [pathname]);

  const isCategoryTypeValid = useCallback(
    (type: string) =>
      CATEGORY_TYPES.includes(type as (typeof CATEGORY_TYPES)[number]),
    []
  );

  const currentProviderId = useMemo(() => {
    if (!providers || !currentProviderTitle) return null;
    return providers.find(
      (provider) =>
        processText(provider?.id) === processText(currentProviderTitle)
    );
  }, [providers, currentProviderTitle]);

  const processedCategories = useMemo(() => {
    if (!categories) return [];
    if (!currentProviderTitle || isCategoryTypeValid(currentProviderTitle))
      return categories;

    const gamesCount = {};

    games.forEach((game) => {
      if (game.filters?.length) {
        game.filters.forEach((filter) => {
          gamesCount[filter] = (gamesCount[filter] || 0) + 1;
        });
      }
    });

    return categories.map((category) => ({
      ...category,
      gamesCount:
        currentProviderTitle === 'favorite'
          ? gamesCount?.[category.id]
          : (currentProviderId?.filters?.[category.id] ?? 0),
    }));
  }, [categories, currentProviderId, games, currentProviderTitle]);

  const categoryOptions = useMemo(() => {
    const totalGamesCount = currentProviderId?.id
      ? currentProviderId?.gamesCount
      : (providers?.reduce((acc, provider) => acc + provider.gamesCount, 0) ??
        0);
    // const bonusCategory = notifications?.bonus?.isActive ? {
    //   id: BONUS_CATEGORY_ID,
    //   imageUrl: CATEGORY_ICON_PATHS.BONUS,
    //   gamesCount: notifications?.bonusInfo?.games?.length,
    // } : null;
    return [
      {
        id: ALL_CATEGORY_ID,
        imageUrl: CATEGORY_ICON_PATHS.ALL,
        imageUrlGray: CATEGORY_ICON_PATHS.ALL_GRAY,
        gamesCount: totalGamesCount,
      },
      // bonusCategory,
      ...processedCategories,
    ].filter(Boolean);
  }, [currentProviderId, processedCategories, providers, notifications]);

  const handleCategoryChange = useCallback(
    (option) => {
      if (!option?.gamesCount && !!currentProviderTitle) return;

      const query = option.id === ALL_CATEGORY_ID ? '' : `?filter=${option.id}`;
      router.push(`${pathname}${query}`);
    },
    [pathname, router, currentProviderTitle]
  );

  const toggleMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen((prev) => !prev);
  }, []);

  const getCategoryImageSrc = useCallback((option) => {
    if (option.id === ALL_CATEGORY_ID) return option.imageUrlGray;
    if (option.id === BONUS_CATEGORY_ID) return option.imageUrl;

    return `${CATEGORY_IMAGE_PATH_PREFIX}${option.id}${CATEGORY_IMAGE_PATH_SUFFIX}`;
  }, []);

  const getOptionDisabledState = useCallback(
    (option, currentProviderTitle) => {
      return (
        (!!option?.gamesCount && !!currentProviderTitle) ||
        (!!currentProviderTitle && isCategoryTypeValid(currentProviderTitle)) ||
        (!!option?.gamesCount && currentProviderTitle !== 'favorite')
      );
    },
    [isCategoryTypeValid]
  );

  const renderOptionLabel = useCallback(
    (id) => t(`common.filter.category.${id}`),
    [t]
  );

  const renderOption = useCallback(
    (option, isSelected, isDrawer = false) => {
      const isOptionDisabled = getOptionDisabledState(
        option,
        currentProviderTitle
      );

      return (
        <OptionWrapper
          $isSelected={isSelected}
          $isDisabled={isOptionDisabled}
          $haveDrawer={isDrawer}
        >
          {!isDrawer && (
            <ImageOption
              src={getCategoryImageSrc(option)}
              $isAll={option.id === ALL_CATEGORY_ID}
              $isActive={option.id === categoryFilter}
              width={IMAGE_SIZES.MOBILE}
              height={IMAGE_SIZES.MOBILE}
              alt="category-icon"
            />
          )}
          <BodyTextBoldSecondary>
            {renderOptionLabel(option.id)}
          </BodyTextBoldSecondary>

          <BodyTextRegularSecondary color={COLOR_SECONDARY}>
            {(isOptionDisabled || !currentProviderTitle) && option.gamesCount}
          </BodyTextRegularSecondary>
          {isSelected && !isDrawer && (
            <OptionCheck>
              <Image
                src={CATEGORY_ICON_PATHS.CHECK}
                width={CHECK_ICON_SIZE}
                height={CHECK_ICON_SIZE}
                alt="Checkmark"
              />
            </OptionCheck>
          )}
        </OptionWrapper>
      );
    },
    [
      categoryFilter,
      currentProviderTitle,
      getCategoryImageSrc,
      getOptionDisabledState,
      renderOptionLabel,
    ]
  );

  const CustomOption = useMemo(
    () =>
      function OptionComponent({ children, ...props }) {
        return (
          <components.Option {...props}>
            {renderOption(props.data, props.isSelected)}
          </components.Option>
        );
      },
    [renderOption]
  );

  const selectComponents = useMemo(
    () => ({
      Control: ({ children, ...props }) => (
        <components.Control {...props}>
          <ControlWrapper>
            <ImageControlWrapper>
              <Image
                src={CATEGORY_ICON_PATHS.FILTERS}
                width={
                  mobileLandscape ? IMAGE_SIZES.MOBILE : IMAGE_SIZES.DESKTOP
                }
                height={
                  mobileLandscape ? IMAGE_SIZES.MOBILE : IMAGE_SIZES.DESKTOP
                }
                alt="Filter icon"
              />
            </ImageControlWrapper>
            {children}
          </ControlWrapper>
        </components.Control>
      ),
      SingleValue: ({ children, ...props }) =>
        mobileLandscape ? null : (
          <components.SingleValue {...props}>
            <EllipsisWrapper>
              <BodyTextBoldPrimary>
                {categoryFilter === 'all'
                  ? t('common.filter.label.category')
                  : renderOptionLabel(categoryFilter)}
              </BodyTextBoldPrimary>
            </EllipsisWrapper>
          </components.SingleValue>
        ),
      DropdownIndicator: () => null,
    }),
    [mobileLandscape, t, categoryFilter]
  );

  if (isCategoriesLoading || isProvidersLoading || isUserLoading) {
    return (
      <SkeletonLoader
        containerClassName="flex-1"
        width={mobileLandscape ? '100%' : 296}
        height={40}
      />
    );
  }

  return (
    <>
      <SelectWrapper
        $isClickable={mobileLandscape}
        onClick={mobileLandscape ? toggleMobileDrawer : undefined}
      >
        <StyledSelect
          value={{ id: categoryFilter }}
          unstyled
          components={{
            ...selectComponents,
            Option: CustomOption,
          }}
          isSearchable={false}
          isDisabled={mobileLandscape}
          classNamePrefix="react-select"
          getOptionLabel={(option) => option.id}
          delimiter={false}
          getOptionValue={(option) => option.id}
          onChange={handleCategoryChange}
          options={categoryOptions}
        />
      </SelectWrapper>

      {mobileLandscape && (
        <Drawer
          onClose={toggleMobileDrawer}
          isCategory
          isOpen={isMobileDrawerOpen}
        >
          <DrawerWrapper>
            <div onClick={toggleMobileDrawer} style={{ height: '100%' }} />
            <OptionsWrapper>
              {categoryOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => {
                    handleCategoryChange(option);
                    toggleMobileDrawer();
                  }}
                >
                  {renderOption(option, option.id === categoryFilter, true)}
                </div>
              ))}
            </OptionsWrapper>
          </DrawerWrapper>
        </Drawer>
      )}
    </>
  );
};

const SelectCategoryWithBreakpointsSlice = withBreakpointsSlice(
  SelectCategory,
  ({ mobileLandscape }) => ({ mobileLandscape })
);

export { SelectCategoryWithBreakpointsSlice as SelectCategory };
